import { categories } from "@sobidogam/catalog";
import {
	formatWon,
	type HouseholdConsumptionState,
	hasHolidayData,
	holidayName,
	kstDateKey,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useEffect, useState } from "react";

const WEEK = ["일", "월", "화", "수", "목", "금", "토"];

function compactWon(value: number): string {
	if (value >= 10_000) {
		const man = value / 10_000;
		return `${man >= 100 ? Math.round(man) : Math.round(man * 10) / 10}만`;
	}
	return value.toLocaleString("ko-KR");
}

/**
 * #36: month calendar with daily totals, recurring payment days and public
 * holidays ("납부일이 공휴일일 경우" 고점 리뷰). Tapping a day lists that day.
 */
export function CalendarView({
	receipts,
	household,
	onTrack,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	onTrack?: (source: string) => void;
}) {
	const today = kstDateKey(new Date());
	const [cursor, setCursor] = useState(() => ({
		year: Number(today.slice(0, 4)),
		month: Number(today.slice(5, 7)),
	}));
	const [selectedDay, setSelectedDay] = useState<string | null>(null);
	useEffect(() => {
		onTrack?.("records");
	}, [onTrack]);
	const lastDay = new Date(Date.UTC(cursor.year, cursor.month, 0)).getUTCDate();
	const firstWeekday = new Date(
		Date.UTC(cursor.year, cursor.month - 1, 1),
	).getUTCDay();
	const prefix = `${cursor.year}-${String(cursor.month).padStart(2, "0")}`;
	const byDay = new Map<string, Receipt[]>();
	for (const receipt of receipts) {
		if (receipt.deletedAt) continue;
		const key = kstDateKey(receipt.transactedAt);
		if (!key.startsWith(prefix)) continue;
		byDay.set(key, [...(byDay.get(key) ?? []), receipt]);
	}
	const recurringByDay = new Map<string, string[]>();
	for (const payment of household.recurringPayments ?? []) {
		if (payment.paused) continue;
		const day = Math.min(lastDay, payment.dayOfMonth);
		const key = `${prefix}-${String(day).padStart(2, "0")}`;
		recurringByDay.set(key, [...(recurringByDay.get(key) ?? []), payment.name]);
	}
	const monthTotal = [...byDay.values()]
		.flat()
		.reduce((sum, receipt) => sum + Math.max(0, receipt.totalAmount), 0);
	const leading = Array.from(
		{ length: firstWeekday },
		(_, index) => `lead-${prefix}-${index}`,
	);
	const days = Array.from(
		{ length: lastDay },
		(_, index) => `${prefix}-${String(index + 1).padStart(2, "0")}`,
	);
	const move = (delta: number) => {
		setSelectedDay(null);
		setCursor(({ year, month }) => {
			const next = month + delta;
			if (next < 1) return { year: year - 1, month: 12 };
			if (next > 12) return { year: year + 1, month: 1 };
			return { year, month: next };
		});
	};
	const dayRecords = selectedDay ? (byDay.get(selectedDay) ?? []) : [];
	return (
		<section
			className="calendar-view"
			aria-label={`${cursor.year}년 ${cursor.month}월 달력`}
		>
			<header className="calendar-head">
				<button
					type="button"
					className="quiet-button"
					onClick={() => move(-1)}
					aria-label="이전 달"
				>
					‹
				</button>
				<div>
					<strong>
						{cursor.year}년 {cursor.month}월
					</strong>
					<span className="amount">{formatWon(monthTotal)}</span>
				</div>
				<button
					type="button"
					className="quiet-button"
					onClick={() => move(1)}
					aria-label="다음 달"
				>
					›
				</button>
			</header>
			{!hasHolidayData(cursor.year) ? (
				<p className="field-hint">공휴일 정보는 2026~2027년까지 있어요.</p>
			) : null}
			<div className="calendar-grid">
				{WEEK.map((name, index) => (
					<span
						className={`calendar-weekday ${index === 0 ? "is-sunday" : ""}`}
						key={name}
					>
						{name}
					</span>
				))}
				{leading.map((key) => (
					<span key={key} className="calendar-blank" aria-hidden="true" />
				))}
				{days.map((key) => {
					const records = byDay.get(key) ?? [];
					const total = records.reduce(
						(sum, receipt) => sum + Math.max(0, receipt.totalAmount),
						0,
					);
					const holiday = holidayName(key);
					const recurring = recurringByDay.get(key) ?? [];
					const weekday = (firstWeekday + Number(key.slice(8)) - 1) % 7;
					const label = [
						`${Number(key.slice(5, 7))}월 ${Number(key.slice(8))}일`,
						holiday,
						recurring.length ? `정기 결제 ${recurring.join(", ")}` : null,
						records.length
							? `${records.length}건 ${formatWon(total)}`
							: "기록 없음",
					]
						.filter(Boolean)
						.join(", ");
					return (
						<button
							type="button"
							key={key}
							className={`calendar-cell ${holiday || weekday === 0 ? "is-holiday" : ""} ${key === today ? "is-today" : ""} ${selectedDay === key ? "is-selected" : ""}`}
							aria-label={label}
							aria-pressed={selectedDay === key}
							onClick={() => setSelectedDay(selectedDay === key ? null : key)}
						>
							<span className="calendar-date">{Number(key.slice(8))}</span>
							{total ? <b>{compactWon(total)}</b> : null}
							<span className="calendar-marks">
								{recurring.length ? (
									<i className="mark-recurring" title="정기 결제" />
								) : null}
								{holiday ? (
									<i className="mark-holiday" title={holiday} />
								) : null}
							</span>
						</button>
					);
				})}
			</div>
			<p className="calendar-legend">
				<span>
					<i className="mark-recurring" /> 정기 결제일
				</span>
				<span>
					<i className="mark-holiday" /> 공휴일
				</span>
			</p>
			{selectedDay ? (
				<div className="calendar-day" aria-live="polite">
					<h3>
						{Number(selectedDay.slice(5, 7))}월 {Number(selectedDay.slice(8))}일
						{holidayName(selectedDay) ? ` · ${holidayName(selectedDay)}` : ""}
					</h3>
					{(recurringByDay.get(selectedDay) ?? []).map((name) => (
						<p className="calendar-recurring" key={name}>
							정기 결제 예정 · {name}
						</p>
					))}
					{dayRecords.length ? (
						<ul>
							{dayRecords.map((receipt) => (
								<li key={receipt.receiptId}>
									<span>
										{receipt.storeDisplay}
										<small>
											{categories.find(
												(category) =>
													category.categoryId === receipt.categoryId,
											)?.nameKo ?? "기타"}
										</small>
									</span>
									<b className="amount">{formatWon(receipt.totalAmount)}</b>
								</li>
							))}
							<li className="calendar-day-total">
								<span>합계</span>
								<b className="amount">
									{formatWon(
										dayRecords.reduce(
											(sum, receipt) => sum + receipt.totalAmount,
											0,
										),
									)}
								</b>
							</li>
						</ul>
					) : (
						<p className="field-hint">이날은 기록이 없어요.</p>
					)}
				</div>
			) : null}
		</section>
	);
}
